import { t } from "@lingui/macro";
import { ShareNetwork, Question, GlobeHemisphereWest, LockKey, Globe, ArrowRight, Copy, Check } from "@phosphor-icons/react";
import { Button, Tooltip, Switch } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTemplateAccess } from "@/client/hooks/use-template-access";
import { useToast } from "@/client/hooks/use-toast";
import { useUser } from "@/client/services/user";
import { useResumeStore } from "@/client/stores/resume";
import { trackResumeEvent } from "@/client/utils/gtm";

export const SharingSection = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const username = user?.username;
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const setValue = useResumeStore((state) => state.setValue);
  const resume = useResumeStore((state) => state.resume);
  const metadata = useResumeStore((state) => {
    try {
      return state.resume.data.metadata;
    } catch {
      return {};
    }
  });

  // Get template access permissions
  const templateTheme = (metadata as any)?.theme;
  const { canShare } = useTemplateAccess(templateTheme);

  const { id, visibility } = resume;
  const url = `${location.origin}/resume/${id}`;
  const isPublic = visibility === "public";

  const onCopy = async () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(url);
      trackResumeEvent.shared();

      toast({
        variant: "default",
        title: t`A link has been copied to your clipboard.`,
        description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
      });
    } else {
      // Fallback: try to use execCommand or show error
      try {
        const input = document.createElement("input");
        input.value = url;
        document.body.append(input);
        input.select();
        document.execCommand("copy");
        input.remove();
        toast({
          variant: "default",
          title: t`A link has been copied to your clipboard.`,
          description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
        });
      } catch {
        toast({
          variant: "error",
          title: t`Copy to clipboard is not supported in this browser.`,
          description: t`Please copy the link manually.`,
        });
      }
    }
  };

  const onVisibilityChange = (checked: boolean) => {
    if (!canShare && checked) {
      // If user tries to enable sharing but doesn't have permission, redirect to pricing
      toast({
        variant: "info",
        title: t`Sharing Requires Basic or Professional Plan`,
        description: t`Public sharing is available for Basic and Professional subscribers. Redirecting to pricing page.`,
      });
      navigate("/pricing");
      return;
    }

    setValue("visibility", checked ? "public" : "private");
  };

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6 border-b border-gray-200 pb-5">
        <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
          <ShareNetwork size={20} className="text-gray-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#16344c]">{t`Sharing`}</h3>
          <p className="text-[11px] text-gray-400">{t`Public link`}</p>
        </div>
        <div className="ml-auto">
          <Tooltip content={t`Learn more about sharing`}>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors">
              <Question size={18} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">
      <div className="space-y-6">
        {/* Hero Graphic */}
        <div className="flex items-center justify-center py-6">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <div className="absolute inset-0 bg-[#268599]/10 rounded-full blur-xl animate-pulse"></div>
            <div className="w-16 h-16 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center z-10 shadow-sm relative">
              <GlobeHemisphereWest weight="duotone" className={cn("text-3xl text-[#268599] transition-opacity", !isPublic && "opacity-30")} />
              {!isPublic && (
                <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm rounded-full">
                  <LockKey weight="fill" className="text-xl text-gray-400" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center space-y-1">
          <h3 className="font-bold text-lg text-[#16344c]">
            {isPublic ? t`Your resume is Live` : t`Your resume is Private`}
          </h3>
          <p className="text-xs text-gray-500 px-4">
            {isPublic
              ? t`Anyone with the link can view your resume.`
              : t`Only you can see this resume. Turn on sharing to make it public.`}
          </p>
        </div>

        <div className="h-px bg-gray-200 w-full"></div>

        {/* Public Toggle */}
        <div className="p-1 rounded-2xl bg-gray-50 border border-gray-200">
          <label
            className={cn(
              "flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 transition-all cursor-pointer group",
              !canShare && "opacity-60 cursor-not-allowed"
            )}
            onClick={(e) => {
              if (canShare) {
                // Prevent default so we can handle it via the switch input specifically
                // e.preventDefault();
              }
            }}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                isPublic ? "bg-[#268599]/10 text-[#268599] border border-[#268599]/30" : "bg-gray-100 border border-gray-200 text-gray-400"
              )}>
                {isPublic ? <Globe weight="fill" className="text-xl" /> : <LockKey weight="fill" className="text-xl" />}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-[#16344c]">{t`Public Access`}</span>
                {!canShare && (
                  <span className="text-[10px] text-[#268599] flex items-center gap-1 group-hover:underline">
                    Upgrade required <ArrowRight weight="bold" size={10} />
                  </span>
                )}
                {canShare && isPublic && <span className="text-[10px] text-[#268599]">Active globally</span>}
                {canShare && !isPublic && <span className="text-[10px] text-gray-400">Disabled</span>}
              </div>
            </div>

            {/* Custom Premium Switch */}
            <div className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full pointer-events-none">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={isPublic}
                disabled={!canShare}
                onChange={(e) => onVisibilityChange(e.target.checked)}
              />
              <div className="h-full w-full rounded-full bg-gray-200 border border-gray-300 transition-all peer-checked:bg-[#268599]/40 peer-checked:border-[#268599]/50"></div>
              <div className="group pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-all peer-checked:translate-x-5 peer-checked:bg-[#268599] shadow-[0_0_10px_rgba(20,184,166,0)] peer-checked:shadow-[0_0_10px_rgba(38,133,153,0.5)]"></div>
            </div>
          </label>
        </div>

        {/* Share Link Box */}
        {isPublic && (
          <div className="space-y-3 animate-in slide-in-from-top-2 fade-in duration-300">
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Share Link</span>

            <div className="flex min-w-0 flex-wrap items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1.5 transition-colors group hover:border-[#268599]/30">
              <div className="min-w-0 max-w-[200px] flex-1 basis-[200px] break-all py-2 pl-3 pr-1 font-mono text-[11px] leading-5 text-gray-500 select-all">
                {url}
              </div>

              <button
                onClick={onCopy}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-lg py-2 pl-3 pr-4 text-xs font-bold transition-all",
                  copied
                    ? "bg-[#268599] text-white shadow-[0_0_15px_rgba(38,133,153,0.3)]"
                    : "bg-gray-100 text-gray-600 hover:bg-[#268599]/10 hover:text-[#268599]"
                )}
              >
                {copied ? <Check weight="bold" size={14} /> : <Copy weight="bold" size={14} />}
                <span>{copied ? t`Copied!` : t`Copy`}</span>
              </button>
            </div>
          </div>
        )}
      </div>
      </div>{/* end white card */}
    </div>
  );
};
